import React from "react";
import { Badge } from "../ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Label } from "../ui/label";
import { Separator } from "../ui/separator";
import {
  Settings,
  Code,
  CheckCircle,
  XCircle,
  Calendar,
  Hash,
} from "lucide-react";
import { PlanTier } from "../../types/subscription";

interface PlanTierViewerProps {
  planTier: PlanTier;
}

const formatKeyLabel = (key: string): string => {
  return key
    .replace(/_/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

export const PlanTierViewer: React.FC<PlanTierViewerProps> = ({ planTier }) => {
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const renderFeatureValue = (value: any, icon: React.ReactNode) => {
    if (typeof value === "boolean") {
      return (
        <div className="flex items-center gap-2">
          {value ? (
            <CheckCircle className="h-4 w-4 text-green-500" />
          ) : (
            <XCircle className="h-4 w-4 text-red-500" />
          )}
          <span>{value ? "Enabled" : "Disabled"}</span>
          {icon}
        </div>
      );
    }

    if (typeof value === "number") {
      return (
        <div className="flex items-center gap-2">
          <span className="font-semibold">{value.toLocaleString()}</span>
          {icon}
        </div>
      );
    }

    return (
      <div className="flex items-center gap-2">
        <span>{String(value)}</span>
        {icon}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Basic Information */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Hash className="h-5 w-5 text-primary" />
            Plan Information
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-sm font-medium text-muted-foreground">
                Plan Name
              </Label>
              <div className="font-semibold text-lg">{planTier.name}</div>
            </div>
            <div>
              <Label className="text-sm font-medium text-muted-foreground">
                Tier Level
              </Label>
              <Badge variant="outline" className="text-sm">
                Level {planTier.tier_level}
              </Badge>
            </div>
            <div>
              <Label className="text-sm font-medium text-muted-foreground">
                Status
              </Label>
              <Badge variant={planTier.is_active ? "default" : "secondary"}>
                {planTier.is_active ? "Active" : "Inactive"}
              </Badge>
            </div>

          </div>

          {planTier.description && (
            <>
              <Separator />
              <div>
                <Label className="text-sm font-medium text-muted-foreground">
                  Description
                </Label>
                <p className="mt-1">{planTier.description}</p>
              </div>
            </>
          )}

          <Separator />
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <Label className="text-sm font-medium text-muted-foreground">
                Created
              </Label>
              <div className="flex items-center gap-2 mt-1">
                <Calendar className="h-4 w-4 text-muted-foreground" />
                <span>{formatDate(planTier.created_at)}</span>
              </div>
            </div>
            <div>
              <Label className="text-sm font-medium text-muted-foreground">
                Last Updated
              </Label>
              <div className="flex items-center gap-2 mt-1">
                <Calendar className="h-4 w-4 text-muted-foreground" />
                <span>{formatDate(planTier.updated_at)}</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Dynamic Feature Modules */}
      {Object.entries(planTier.features).map(([moduleName, moduleData]) => {
        if (!moduleData || typeof moduleData !== "object") return null;
        const fields = Object.entries(moduleData as Record<string, unknown>);
        if (fields.length === 0) return null;

        return (
          <Card key={moduleName}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Settings className="h-5 w-5 text-blue-500" />
                {formatKeyLabel(moduleName)} Features
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {fields.map(([key, val]) => (
                  <div
                    key={key}
                    className="flex items-center justify-between p-3 bg-muted/50 rounded-lg"
                  >
                    <div>
                      <Label className="text-sm font-medium">
                        {formatKeyLabel(key)}
                      </Label>
                    </div>
                    {renderFeatureValue(val, null)}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        );
      })}

      {/* Pricing Information */}
      {(planTier.price_monthly != null || planTier.price_yearly != null) && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Code className="h-5 w-5 text-purple-500" />
              Pricing
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {planTier.price_monthly != null && (
              <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                <Label className="text-sm font-medium">Monthly Price</Label>
                <span className="font-semibold">${planTier.price_monthly}/mo</span>
              </div>
            )}
            {planTier.price_yearly != null && (
              <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                <Label className="text-sm font-medium">Yearly Price</Label>
                <span className="font-semibold">${planTier.price_yearly}/yr</span>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};
